import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { chargeSourceKey, chargeStatusKey, currentUtcMonth, sumDecimalStrings } from '@entities/aml-billing';
import { swrKeys } from '@shared/api/swr/keys';
import { formatDateTime } from '@shared/lib/format-date';
import { useApiServices } from '@shared/lib/use-api-services';
import {
  Label,
  MonthPicker,
  Skeleton,
  SkeletonStatus,
  StatusBanner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TypographyMuted,
  TypographySmall,
} from '@shared/ui';

export function MerchantAmlCharges() {
  const { t } = useTranslation('cabinet');
  const services = useApiServices();
  const [month, setMonth] = useState(currentUtcMonth());
  const charges = useSWR(swrKeys.aml.myCharges(month), () => services.aml.getMyCharges(month));
  const rows = charges.data?.data ?? [];
  const accrued = sumDecimalStrings(rows.filter((row) => row.status === 'ACCRUED').map((row) => row.price));
  const invoiced = sumDecimalStrings(rows.filter((row) => row.status === 'INVOICED').map((row) => row.price));

  return (
    <section className="surface-card space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <TypographySmall>{t('amlBilling.merchantTitle')}</TypographySmall>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('amlBilling.merchantTotals', { accrued, invoiced })}
          </p>
        </div>
        <div className="space-y-1">
          <Label htmlFor="merchant-aml-month">{t('amlBilling.month')}</Label>
          <MonthPicker id="merchant-aml-month" value={month} onChange={setMonth} />
        </div>
      </div>
      {charges.error ? <StatusBanner variant="destructive">{t('workspace.loadError')}</StatusBanner> : null}
      {charges.isLoading && !rows.length ? (
        <SkeletonStatus label={t('workspace.loading')}>
          <Skeleton className="h-40 w-full" />
        </SkeletonStatus>
      ) : null}
      {!charges.isLoading && !charges.error && !rows.length ? <TypographyMuted className="block py-8 text-center">{t('amlBilling.empty')}</TypographyMuted> : null}
      {rows.length ? (
        <div className="min-w-0 overflow-x-auto">
          <Table className="min-w-[40rem]">
            <TableHeader>
              <TableRow>
                <TableHead>{t('withdrawals.columns.date')}</TableHead>
                <TableHead>{t('amlBilling.sourceColumn')}</TableHead>
                <TableHead>{t('withdrawals.columns.network')}</TableHead>
                <TableHead>{t('withdrawals.columns.amount')}</TableHead>
                <TableHead>{t('withdrawals.columns.status')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="whitespace-nowrap">{formatDateTime(row.createdAt)}</TableCell>
                  <TableCell>{t(chargeSourceKey(row.sourceType))}</TableCell>
                  <TableCell>{row.network}</TableCell>
                  <TableCell className="tabular-nums">{row.price} {row.currency}</TableCell>
                  <TableCell>{t(chargeStatusKey(row.status, row.statusReason))}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}
    </section>
  );
}
